<template>
  <section class="policy-workspace">
    <div class="policy-head" v-if="currPlan">
      <a href="#" class="policy-back" @click.prevent="handleClear()">
        <i class="material-icons">keyboard_return</i>
        All Plans
      </a>
      <div class="policy-head-current">
        <label class="active teal-text">Selected Plan</label>
        <h5>{{ currPlan.name }}</h5>
      </div>
    </div>

    <div class="policy-head" v-else>
      <div>
        <label class="active teal-text">Plans</label>
        <h5>Client Plan Catalogue</h5>
        <p>Premiums, active enrollee mix, and policy controls for this client.</p>
      </div>
    </div>

    <div class="policy-list">
      <article
        v-for="plan in plans"
        :key="plan.id"
        class="policy-card"
        :class="{ muted: !canReviewPlan(plan) }"
        v-show="!currPlan || plan.id == currPlan.id"
      >
        <div class="policy-card-main">
          <div class="policy-title-row">
            <div class="policy-name-block">
              <div>
                <label class="active teal-text">Plan</label>
                <h6>
                  {{ plan.name }}
                  <small v-if="isCustomized(plan)">Customized</small>
                </h6>
                <p>{{ planDescription(plan) }}</p>
              </div>
            </div>

            <span
              class="policy-state"
              :class="{ off: !canReviewPlan(plan) }"
            >
              <i class="material-icons">{{
                canReviewPlan(plan) ? "verified_user" : "block"
              }}</i>
              {{ canReviewPlan(plan) ? "Available" : "Suspended" }}
            </span>
          </div>

          <div class="policy-facts">
            <div class="policy-fact premium">
              <span>Individual Premium</span>
              <strong>{{ premiumText(plan, "i") }}</strong>
            </div>
            <div class="policy-fact premium">
              <span>Family Premium</span>
              <strong>{{ premiumText(plan, "f") }}</strong>
            </div>
            <div class="policy-fact">
              <span>Active Individuals</span>
              <strong
                >{{ plan.individuals }} <em>of</em>
                {{ client.principals_count }}</strong
              >
            </div>
            <div class="policy-fact">
              <span>Active Families</span>
              <strong
                >{{ plan.familys }} <em>of</em>
                {{ client.principals_count }}</strong
              >
            </div>
          </div>
        </div>

        <div class="policy-card-actions">
          <template v-if="canReviewPlan(plan)">
            <a
              href="#"
              class="icon-action dropdown-trigger"
              :data-target="`dropmenu_${plan.id}`"
            >
              <i class="material-icons" title="review">more_vert</i>
            </a>

            <ul :id="`dropmenu_${plan.id}`" class="dropdown-content">
              <li @click.prevent="handleMenu({ type: 'view', value: plan })">
                <a href="#">View Details</a>
              </li>
              <li class="divider" tabindex="-1"></li>
              <li @click.prevent="handleMenu({ type: 'update', value: plan })">
                <a href="#">Update Policy</a>
              </li>
              <li class="divider" tabindex="-1"></li>
              <li @click.prevent="handleMenu({ type: 'status', value: plan })">
                <a href="#">Suspend Policy</a>
              </li>
            </ul>
          </template>

          <a
            v-else
            href="#"
            class="icon-action restore"
            @click.prevent="handleMenu({ type: 'status', value: plan })"
          >
            <i class="material-icons" title="restore plan">settings_power</i>
          </a>
        </div>
      </article>
    </div>

    <!-- Edit Plan-->
    <div v-if="currPlan && editPolicy">
      <PolicyEditDiv
        :cat="cat"
        :policy="currPlan"
        :client="client"
        @reset="handleUpdateComplete"
        v-if="editPolicy"
      />
    </div>

    <!-- Status Plan-->
    <div v-if="currPlan && statusPolicy">
      <PolicyStatusDiv
        :cat="cat"
        :policy="currPlan"
        :client="client"
        @reset="handleUpdateStatus"
        v-if="statusPolicy"
      />
    </div>

    <!-- Benefits -->
    <div class="benefit-section" v-if="currPlan && !editPolicy && !statusPolicy">
      <div class="progress" v-if="isLoading">
        <div class="indeterminate"></div>
      </div>

      <template v-else>
        <div class="benefit-head">
          <label class="active teal-text">Benefits</label>
          <h6>Plan Benefits</h6>
        </div>

        <div class="benefit-card" v-for="benefit in benefits" :key="benefit.id">
          <div>
            <h6>{{ benefit.item }}</h6>
            <span>{{ benefit.description }}</span>
          </div>
        </div>

        <div class="empty-state" v-if="!benefits?.length">
          <i class="material-icons">fact_check</i>
          <h6>No benefits found</h6>
        </div>

        <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
      </template>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onUpdated, computed } from "vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItemsOfItemsOfItems from "@/composables/getItemsOfItemsOfItems";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import PolicyEditDiv from "@/components/client/onboard/PolicyReviewEditOnboardComponent.vue";
import PolicyStatusDiv from "@/components/client/onboard/PolicyReviewStatusOnboardComponent.vue";
import nativeUi from "@/ui/native-ui";

export default {
  name: "PolicyClientComponent",
  components: { PaginationDiv, PolicyEditDiv, PolicyStatusDiv },
  props: ["cat", "client"],
  emits: ["reset"],
  setup(props, context) {
    const isLoading = ref(false);
    const response = ref(null);
    const responsePlans = ref(props.client.plans);
    const currUrl = ref(null);
    const currHook = ref(null);
    const error = ref(null);
    const currPlan = ref(null);
    const addPolicy = ref(false);
    const editPolicy = ref(false);
    const statusPolicy = ref(false);

    // get function
    const getBenefitsFromApi = (table, planId, clientId) => {
      (async () => {
        const { res, err } = await getItemsOfItemsOfItems(
          table,
          planId,
          clientId
        );

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // close
        isLoading.value = false;
      })();
    };

    // get benefits
    const getBenefits = () => {
      // start laoding
      isLoading.value = true;
      // set current Url
      currUrl.value = "benefits/plan/search";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getBenefitsFromApi(currUrl.value, currPlan.value.id, props.client.id);
    };

    // to naira
    const changeToNaira = (premiums, type) => {
      // sort value
      let val;
      let valFixed = false;
      premiums.forEach((x) => {
        if (x.type == type && !valFixed) {
          val = x.amount;
          valFixed = true;
        }
      });
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    const firstPremiumAmount = (plan) => {
      return Number(plan?.premiums?.[0]?.amount || 0);
    };

    const canReviewPlan = (plan) => {
      if (props.cat == "tpa") {
        return firstPremiumAmount(plan) == 0;
      }

      return firstPremiumAmount(plan) > 0;
    };

    const isCustomized = (plan) => {
      return !!plan.premiums?.filter((x) => x.scope == props.client.id)?.length;
    };

    const planDescription = (plan) => {
      if (props.cat == "tpa") {
        return "Reference Plan";
      }

      return isCustomized(plan) ? "Customized Plan" : plan.description;
    };

    const premiumText = (plan, type) => {
      if (props.cat == "tpa") {
        return "N/A";
      }

      return `NGN ${changeToNaira(plan.premiums, type)}`;
    };

    // starter
    onMounted(() => {
      initializeDropdown();
    });

    // more
    onUpdated(() => {
      initializeDropdown();
    });

    const initializeDropdown = () => {
      var elems = document.querySelectorAll(".dropdown-trigger");
      nativeUi.Dropdown.init(elems, {});
    };

    // switch page
    const handlePage = (page) => {
      // start laoding
      isLoading.value = true;
      // get new page items from function
      getBenefitsFromApi(
        currUrl.value,
        currPlan.value.id,
        `${props.client.id}${currHook.value}page=${page}`
      );
    };

    // handle curr status
    const handleClear = () => {
      // view All plans
      addPolicy.value = false;
      editPolicy.value = false;
      currPlan.value = null;
    };

    // handle add
    const handleAdd = () => {
      handleClear();
    };

    // handle update complete
    const handleUpdateStatus = (val) => {
      // update main
      if (val) {
        // load now
        responsePlans.value = val.plans;
        // let parent have the update
        context.emit("reset", val);
      }

      // clear all
      currPlan.value = null;
      addPolicy.value = false;
      editPolicy.value = false;
      statusPolicy.value = false;
    };

    // handle update complete
    const handleUpdateComplete = (val) => {
      // update main
      if (val) {
        // load now
        responsePlans.value = val.plans;
        // reload benefits
        getBenefits();
        // let parent have the update
        context.emit("reset", val);
      }

      // clear all
      addPolicy.value = false;
      editPolicy.value = false;
    };

    // handle menu
    const handleMenu = (val) => {
      // set current plan
      currPlan.value = val.value;

      // reset view
      if (val.type == "view") {
        editPolicy.value = false;
        statusPolicy.value = false;
      }

      // set edit
      if (val.type == "update") {
        statusPolicy.value = false;
        editPolicy.value = true;
      }

      // set status
      if (val.type == "status") {
        editPolicy.value = false;
        statusPolicy.value = true;
      }

      // get benfits
      getBenefits();
    };

    // output results (plans)
    const plans = computed(() => {
      if (!responsePlans.value?.length) {
        return [];
      } else {
        return responsePlans.value;
      }
    });

    // output results
    const benefits = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      handleClear,
      handleAdd,
      handleUpdateComplete,
      handleUpdateStatus,
      handleMenu,
      handlePage,
      changeToNaira,
      canReviewPlan,
      isCustomized,
      planDescription,
      premiumText,
      error,
      plans,
      addPolicy,
      editPolicy,
      statusPolicy,
      isLoading,
      currPlan,
      benefits,
      allPages,
    };
  },
};
</script>

<style scoped>
.policy-workspace {
  margin-top: 8px;
}

.policy-head {
  align-items: center;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 24px;
}

.policy-head h5,
.benefit-head h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.policy-head h5 {
  font-size: 1.18rem;
  font-weight: 650;
  line-height: 1.25;
}

.policy-head p {
  color: #607d8b;
  font-size: 0.94rem;
  line-height: 1.5;
  margin: 5px 0 0;
}

.policy-back {
  align-items: center;
  color: #607d8b;
  display: inline-flex;
  font-weight: 700;
  gap: 7px;
}

.policy-back i {
  font-size: 20px;
}

.policy-head-current {
  text-align: right;
}

.policy-list {
  display: grid;
  gap: 14px;
}

.policy-card {
  align-items: center;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 148px;
  padding: 24px;
}

.policy-card.muted {
  background: #fbfbfb;
}

.policy-card:hover {
  border-color: #e0e0e0;
}

.policy-card-main {
  min-width: 0;
}

.policy-title-row {
  align-items: flex-start;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  padding-bottom: 15px;
}

.policy-name-block {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  min-width: 0;
}

.icon-action {
  align-items: center;
  border-radius: 50%;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
}

.policy-name-block h6 {
  color: #263238;
  font-size: 1.12rem;
  font-weight: 700;
  margin: 3px 0 0;
}

.policy-name-block h6 small {
  background: #eceff1;
  border-radius: 12px;
  color: #607d8b;
  font-size: 0.72rem;
  font-weight: 700;
  margin-left: 6px;
  padding: 3px 8px;
  vertical-align: middle;
}

.policy-name-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 7px 0 0;
}

.policy-state {
  align-items: center;
  background: #f7fbf9;
  border: 1px solid #e3f0ec;
  border-radius: 20px;
  color: #2e7d32;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0;
  padding: 6px 11px;
}

.policy-state i {
  font-size: 17px;
}

.policy-state.off {
  background: #f5f6f7;
  border-color: #e5e8ea;
  color: #607d8b;
}

.policy-facts {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  padding-top: 16px;
}

.policy-fact {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  min-height: 74px;
  padding: 12px 14px;
}

.policy-fact:first-child {
  border-left: 1px solid #eeeeee;
  padding-left: 14px;
}

.policy-fact span {
  color: #78909c;
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.policy-fact strong {
  color: #263238;
  display: block;
  font-size: 1.08rem;
  font-weight: 700;
}

.policy-fact em {
  color: #90a4ae;
  font-style: normal;
  font-weight: 600;
}

.policy-fact.premium strong {
  color: #00695c;
}

.policy-fact.premium {
  background: #ffffff;
  border-color: #eeeeee;
}

.policy-card-actions {
  align-items: center;
  display: flex;
}

.icon-action {
  background: #ffffff;
  border: 1px solid #eeeeee;
  color: #607d8b;
  height: 42px;
  width: 42px;
}

.icon-action.restore {
  background: #fff8e1;
  color: #ef6c00;
}

.icon-action i {
  font-size: 21px;
}

.policy-card:hover .icon-action {
  background: #eceff1;
  color: #263238;
}

.benefit-section {
  margin-top: 12px;
}

.benefit-head {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 17px 18px;
}

.benefit-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 15px 18px;
}

.benefit-card h6 {
  color: #263238;
  font-weight: 700;
  margin: 0 0 4px;
}

.benefit-card span {
  color: #607d8b;
}

.empty-state {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  color: #607d8b;
  padding: 30px 16px;
  text-align: center;
}

.empty-state i {
  color: #90a4ae;
  font-size: 34px;
}

.empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 8px 0 0;
}

.progress {
  margin-top: -14px;
}

@media (max-width: 950px) {
  .policy-facts {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }
}

@media (max-width: 700px) {
  .policy-card {
    grid-template-columns: minmax(0, 1fr);
  }

  .policy-card-actions {
    justify-content: flex-end;
  }

  .policy-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .policy-head-current {
    text-align: left;
  }
}

@media (max-width: 560px) {
  .policy-facts {
    grid-template-columns: 1fr;
  }
}
</style>
